import { hasScopesPermission } from '@tribeplatform/gql-client/lib'
import type { PostType } from '@tribeplatform/gql-client/types'
import {
  useArchivePostTypeField,
  useNetwork,
  useUnarchivePostTypeField,
} from '@tribeplatform/react-sdk/hooks'
import { confirm } from '@tribeplatform/react-ui-kit/Dialog'
import { Dropdown } from '@tribeplatform/react-ui-kit/Dropdown'
import { SvgIcon } from '@tribeplatform/react-ui-kit/Icon'
import { toast } from '@tribeplatform/react-ui-kit/Toast'

import { T } from '../../i18n/components/T.js'
import { useI18n } from '../../i18n/providers/I18nProvider.js'

interface Props {
  fieldId: string
  postType: PostType
  openManageFieldModal: () => void
}
export const FieldOptions = ({
  fieldId,
  postType,
  openManageFieldModal,
}: Props) => {
  const { $t } = useI18n()
  const { data: network } = useNetwork()
  const { mutateAsync: archive } = useArchivePostTypeField()
  const { mutateAsync: unarchive } = useUnarchivePostTypeField()
  const archived = !!postType.postFields.fields.find(
    ({ key }) => key === fieldId,
  )?.archived
  const [
    canUpdatePostTypeField,
    canArchivePostTypeField,
    canUnarchivePostTypeField,
  ] = hasScopesPermission(network, [
    'updatePostTypeField',
    'archivePostTypeField',
    'unarchivePostTypeField',
  ])

  const archiveField = async () => {
    try {
      const confirmed = await confirm({
        title: $t({
          defaultMessage: 'Are you sure you want to archive this field?',
          id: 'Admin.CMSField.Archive.Confirm',
        }),
        proceedLabel: $t({
          defaultMessage: 'Archive',
          id: 'Generics.Archive',
        }),
        cancelLabel: $t({
          defaultMessage: 'Cancel',
          id: 'Generics.Cancel',
        }),
      })
      if (!confirmed) {
        return
      }

      await archive({ id: postType.id, key: fieldId })
      toast({
        title: $t({
          defaultMessage: 'Successfully archived field',
          id: 'Admin.CMSField.Archived.Success',
        }),
        status: 'success',
      })
    } catch (error) {
      toast({
        title: $t({
          defaultMessage: 'Could not archive field',
          id: 'Admin.CMSField.Archived.Error',
        }),
        status: 'error',
      })
    }
  }

  const unarchiveField = async () => {
    try {
      await unarchive({ id: postType.id, key: fieldId })
      toast({
        title: $t({
          defaultMessage: 'Successfully unarchived field',
          id: 'Admin.CMSField.Unarchived.Success',
        }),
        status: 'success',
      })
    } catch (error) {
      toast({
        title: $t({
          defaultMessage: 'Could not unarchive field',
          id: 'Admin.CMSField.Unarchived.error',
        }),
        status: 'error',
      })
    }
  }

  return (
    <Dropdown.Items>
      {!archived && canArchivePostTypeField && (
        <Dropdown.Item
          leadingIcon={<SvgIcon name="archive" />}
          onClick={archiveField}
        >
          <T id="Generics.Archive" defaultMessage="Archive" />
        </Dropdown.Item>
      )}
      {archived && canUnarchivePostTypeField && (
        <Dropdown.Item
          leadingIcon={<SvgIcon name="reverse-left" />}
          onClick={unarchiveField}
        >
          <T id="Generics.Unarchive" defaultMessage="Unarchive" />
        </Dropdown.Item>
      )}
      {canUpdatePostTypeField && (
        <Dropdown.Item
          leadingIcon={<SvgIcon name="cog" />}
          onClick={openManageFieldModal}
        >
          <T id="Generics.Settings" defaultMessage="Settings" />
        </Dropdown.Item>
      )}
    </Dropdown.Items>
  )
}
